import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive as ArchiveIcon, Download, Search, ShieldCheck } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Select,
  useToast,
} from '@solara/ui-shared';
import { useArchive, useTenants } from '@solara/api-client/react';
import { adaptTenants } from '../data/adapters';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

export default function ArchivePage() {
  const { t } = useTranslation('admin');
  const { locale } = useI18n();
  const { toast } = useToast();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });

  const [number, setNumber] = useState('');
  const [trn, setTrn] = useState('');
  const [tenantId, setTenantId] = useState<string>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [legal, setLegal] = useState<'all' | 'yes' | 'no'>('all');

  const archiveQuery = useArchive({
    tenantId: tenantId === 'all' ? undefined : tenantId,
    limit: 200,
  });
  const tenantsQuery = useTenants();
  const tenants = useMemo(() => adaptTenants(tenantsQuery.data), [tenantsQuery.data]);
  const records = archiveQuery.data ?? [];

  const results = useMemo(() => {
    return records.filter((rec) => {
      if (number && !rec.id.toLowerCase().includes(number.toLowerCase())) return false;
      if (tenantId !== 'all' && rec.tenantId !== tenantId) return false;
      if (trn) return false;
      const sealed = (rec.sealedAt ?? '').slice(0, 10);
      if (from && sealed && sealed < from) return false;
      if (to && sealed && sealed > to) return false;
      void minAmount; void maxAmount; void legal;
      return true;
    });
  }, [records, number, tenantId, trn, from, to, minAmount, maxAmount, legal]);

  function exportAll(fmt: 'csv' | 'pdf') {
    if (results.length === 0) return;
    toast({
      title: t('archive.export.queued', { format: fmt.toUpperCase() }),
      description: t('archive.export.queuedBody', { count: results.length }),
      tone: 'info',
    });
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('archive.title')}</h1>
        <p className="text-fg-muted">{t('archive.subtitle')}</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t('archive.search.title')}</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-4 md:grid-cols-3">
          <Input
            label={t('archive.search.invoiceNumber')}
            placeholder="INV-2026-…"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            startIcon={<Search className="h-4 w-4" />}
          />
          <Select
            label={t('archive.search.tenant')}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            options={[
              { value: 'all', label: t('archive.search.allTenants') },
              ...tenants.map((tn) => ({ value: tn.tenantId, label: tn.companyNameEn })),
            ]}
          />
          <Input
            label={t('archive.search.trn')}
            placeholder="100000000…"
            value={trn}
            onChange={(e) => setTrn(e.target.value.replace(/\s/g, ''))}
            inputMode="numeric"
            className="num"
            dir="ltr"
          />
          <Input
            label={t('archive.search.minAmount')}
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            inputMode="decimal"
            className="num text-end"
            dir="ltr"
          />
          <Input
            label={t('archive.search.maxAmount')}
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            inputMode="decimal"
            className="num text-end"
            dir="ltr"
          />
          <Select
            label={t('archive.search.legalHold')}
            value={legal}
            onChange={(e) => setLegal(e.target.value as 'all' | 'yes' | 'no')}
            options={[
              { value: 'all', label: t('archive.search.all') },
              { value: 'yes', label: t('archive.search.yes') },
              { value: 'no', label: t('archive.search.no') },
            ]}
          />
          <Input
            label={t('archive.search.from')}
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            dir="ltr"
          />
          <Input
            label={t('archive.search.to')}
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            dir="ltr"
          />
        </CardBody>
        <div className="px-6 pb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm text-fg-muted">
              {t('archive.search.results', { count: results.length, total: records.length })}
            </span>
            <span className="text-xs text-fg-subtle">{t('archive.exportLimit')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => exportAll('csv')} className="gap-2">
              <Download className="h-4 w-4" /> {t('archive.export.csv')}
            </Button>
            <Button variant="outline" onClick={() => exportAll('pdf')} className="gap-2">
              <Download className="h-4 w-4" /> {t('archive.export.pdf')}
            </Button>
          </div>
        </div>
      </Card>

      {archiveQuery.isLoading ? (
        <LoadingBlock />
      ) : archiveQuery.error ? (
        <ErrorBanner error={archiveQuery.error} />
      ) : results.length === 0 ? (
        <EmptyState icon={<ArchiveIcon className="h-6 w-6" />} title={t('archive.empty')} />
      ) : (
        <Card>
          <CardBody>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle">
                    <th className="text-start font-medium pb-2">{t('archive.table.invoice')}</th>
                    <th className="text-start font-medium pb-2">{t('archive.table.tenant')}</th>
                    <th className="text-start font-medium pb-2">{t('archive.table.issued')}</th>
                    <th className="text-end font-medium pb-2">{t('archive.table.gross')}</th>
                    <th className="text-start font-medium pb-2">{t('archive.table.retentionUntil')}</th>
                    <th className="text-start font-medium pb-2">{t('archive.table.hashChain')}</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {results.map((rec) => {
                    const tenant = tenants.find((tn) => tn.tenantId === rec.tenantId);
                    return (
                      <tr key={rec.id}>
                        <td className="py-3">
                          <div className="font-medium font-mono text-xs">{rec.id.slice(0, 8)}…</div>
                        </td>
                        <td className="py-3 truncate max-w-[20ch]">
                          <div>{tenant?.companyNameEn ?? rec.tenantId.slice(0, 8)}</div>
                        </td>
                        <td className="py-3 num">{dateFmt.format(new Date(rec.sealedAt))}</td>
                        <td className="py-3 text-end num">{aedFmt.format(0)}</td>
                        <td className="py-3 num">{dateFmt.format(new Date(rec.retainUntil))}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-1.5 text-xs">
                            <ShieldCheck className="h-3.5 w-3.5 text-success" />
                            <code className="text-[10px] truncate max-w-[14ch]">
                              {rec.sha256.slice(0, 10)}…
                            </code>
                          </div>
                        </td>
                        <td className="py-3">
                          <Button variant="ghost" size="sm" onClick={() => toast({ title: rec.id, tone: 'info' })}>
                            {t('archive.table.open')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
